import {type AppBskyFeedDefs, type AtpAgent} from '@atproto/api'

import {BRAND_DISCOVER_FEED} from '#/lib/constants'
import {CustomFeedAPI} from './custom'
import {FollowingFeedAPI} from './following'
import {ListFeedAPI} from './list'
import {type FeedAPI, type FeedAPIResponse} from './types'

// HACK
// the feed API does not include any facilities for passing down
// non-post elements. adding that is a bit of a heavy lift, and we
// have just one temporary usecase for it: flagging when the home feed
// falls back to discover.
// we use this fallback marker post to drive this instead. see Feed.tsx
// for the usage.
// -prf
export const FALLBACK_MARKER_POST: AppBskyFeedDefs.FeedViewPost = {
  post: {
    uri: 'fallback-marker-post',
    cid: 'fake',
    record: {},
    author: {
      did: 'did:fake',
      handle: 'fake.com',
    },
    indexedAt: new Date().toISOString(),
  },
}

/**
 * The feed the Following timeline spills into once it runs dry. Brand-aware:
 * Bluesky's Discover for cooey/yeet/eagledrive, the district-curated list for
 * ccisd, and `null` for any brand that opts out of a fallback entirely (the
 * Following feed then simply ends).
 */
function createDiscoverAPI({
  agent,
  userInterests,
}: {
  agent: AtpAgent
  userInterests?: string
}): FeedAPI | null {
  if (!BRAND_DISCOVER_FEED) return null
  if (BRAND_DISCOVER_FEED.kind === 'list') {
    return new ListFeedAPI({
      agent,
      feedParams: {list: BRAND_DISCOVER_FEED.uri},
    })
  }
  return new CustomFeedAPI({
    agent,
    feedParams: {feed: BRAND_DISCOVER_FEED.uri},
    userInterests,
  })
}

export class HomeFeedAPI implements FeedAPI {
  agent: AtpAgent
  following: FollowingFeedAPI
  discover: FeedAPI | null
  usingDiscover = false
  itemCursor = 0
  userInterests?: string

  constructor({
    userInterests,
    agent,
  }: {
    userInterests?: string
    agent: AtpAgent
  }) {
    this.agent = agent
    this.following = new FollowingFeedAPI({agent})
    this.discover = createDiscoverAPI({agent})
    this.userInterests = userInterests
  }

  reset() {
    this.following = new FollowingFeedAPI({agent: this.agent})
    this.discover = createDiscoverAPI({
      agent: this.agent,
      userInterests: this.userInterests,
    })
    this.usingDiscover = false
    this.itemCursor = 0
  }

  async peekLatest(): Promise<AppBskyFeedDefs.FeedViewPost> {
    if (this.usingDiscover && this.discover) {
      return this.discover.peekLatest()
    }
    return this.following.peekLatest()
  }

  async fetch({
    cursor,
    limit,
  }: {
    cursor: string | undefined
    limit: number
  }): Promise<FeedAPIResponse> {
    if (!cursor) {
      this.reset()
    }

    let returnCursor
    let posts: AppBskyFeedDefs.FeedViewPost[] = []

    if (!this.usingDiscover) {
      const res = await this.following.fetch({cursor, limit})
      returnCursor = res.cursor
      posts = posts.concat(res.feed)
      // Only announce the fallback if this brand actually has one; without a
      // discover feed the Following timeline just ends.
      if (!returnCursor && this.discover) {
        cursor = ''
        posts.push(FALLBACK_MARKER_POST)
        this.usingDiscover = true
      }
    }

    if (this.usingDiscover && this.discover && !__DEV__) {
      const res = await this.discover.fetch({cursor, limit})
      returnCursor = res.cursor
      posts = posts.concat(res.feed)
    }

    return {
      cursor: returnCursor,
      feed: posts,
    }
  }
}
